import { memo, useMemo } from "react";
import { useTranslations } from "next-intl";
import { useReducedMotion } from "framer-motion";
import { useRotatingStatus } from "@/lib/ai/useRotatingStatus";
import { AiCharacterRiv } from "../AiCharacterRiv";
import { AiOrb } from "../AiOrb";
import { AI_CHARACTER_ENABLED } from "../aiCharacterConfig";

/**
 * Pre-first-token indicator: the Rive character (or a spinner under
 * reduced-motion / when the mascot is disabled) + rotating "Thinking…" copy +
 * a subtle three-dot shimmer. Replaces the old inline spinner span. The SR
 * announcer stays a single atomic "Thinking…" elsewhere — this is visual only.
 */
export const ThinkingIndicator = memo(function ThinkingIndicator({
  character = true,
}: {
  character?: boolean;
}) {
  const t = useTranslations("aiAssistant");
  const reduceMotion = useReducedMotion();
  const showCharacter = character && AI_CHARACTER_ENABLED && !reduceMotion;
  const messages = useMemo(
    () => [t("thinkingRotate0"), t("thinkingRotate1"), t("thinkingRotate2")],
    [t],
  );
  const copy = useRotatingStatus(messages);
  return (
    <span className="flex items-center gap-1.5 font-tertiary">
      {showCharacter ? (
        <AiCharacterRiv mode="thinking" width="w-8" height="h-8" className="shrink-0 -ml-1" />
      ) : (
        <AiOrb size="h-6 w-6" iconSize={13} />
      )}
      <span>{copy}</span>
      {!reduceMotion && (
        <span aria-hidden className="inline-flex items-center gap-0.5">
          <span className="h-1 w-1 rounded-full bg-current opacity-60 animate-bounce [animation-delay:0ms]" />
          <span className="h-1 w-1 rounded-full bg-current opacity-60 animate-bounce [animation-delay:150ms]" />
          <span className="h-1 w-1 rounded-full bg-current opacity-60 animate-bounce [animation-delay:300ms]" />
        </span>
      )}
    </span>
  );
});
